// VoiceHelper.js - High-quality premium male voice implementation
// This is a standalone module that can be imported into your Chatbot.js

// Premium TTS service URL from StreamElements
const PREMIUM_TTS_URL = 'https://api.streamelements.com/kappa/v2/speech';

// List of premium male voices available (excluding Brian)
const PREMIUM_MALE_VOICES = [
  'Joey',     // Default - Natural, friendly male voice
  'Matthew',  // Smooth, warm male voice
  'Justin',   // Clear, confident male voice
  'Russell',  // Authoritative male voice
  'Kevin'     // Warm, engaging male voice
];

const VoiceHelper = {
  // Main function to speak text with high-quality male voice
  speakText: (text) => {
    if (!text?.trim()) return;
    
    // 1. Detect the language from the text.
    const language = VoiceHelper.detectLanguage(text);
    console.log(`>>>>>> VoiceHelper: Detected language: ${language}`);

    // 2. Process text for better speech quality
    text = VoiceHelper.processTextForSpeech(text);

    // Remove asterisks for Malay and Chinese to prevent them being spoken aloud
    if (language === 'ms' || language === 'zh') {
      text = text.replace(/\*/g, '');
    }

    // 3. Create or get the master audio element.
    let audioElement = document.getElementById('tts-audio-element');
    if (!audioElement) {
      audioElement = document.createElement('audio');
      audioElement.id = 'tts-audio-element';
      audioElement.style.display = 'none';
      document.body.appendChild(audioElement);
    }
    audioElement.pause(); // Stop any currently playing audio

    // 4. Speak using the appropriate service based on the detected language.
    if (language === 'en') {
      console.log('>>>>>> VoiceHelper: Using premium male voices for English.');
      const encodedText = encodeURIComponent(text.substring(0, 300));
      VoiceHelper.tryVoiceOptions(audioElement, encodedText);
    } else {
      console.log(`>>>>>> VoiceHelper: Using Google TTS for ${language}.`);
      VoiceHelper.speakNonEnglish(audioElement, text, language);
    }
  },

  // Detects the language of a given text string.
  // Supports English, Chinese (Simplified/Traditional), and Malay.
  detectLanguage: (text) => {
    // Chinese characters (covers both Simplified and Traditional)
    if (/[一-龥]/.test(text)) return 'zh';
    // Urdu characters (Arabic script)
    if (/[؀-ۿ]/.test(text)) return 'ur';
    // A more robust heuristic for Malay. We look for common words that are less likely to appear in English.
    const malayWords = ['saya', 'anda', 'bagaimana', 'boleh', 'bantuan', 'perjalanan', 'destinasi', 'atau', 'dengan', 'untuk'];
    const wordInText = text.toLowerCase().split(/\s+/);
    if (wordInText.some(word => malayWords.includes(word))) return 'ms';
    // Default to English
    return 'en';
  },

  // Speaks non-English text using the premium Google Cloud Text-to-Speech API.
  speakNonEnglish: async (audioElement, text, language) => {
    const apiKey = process.env.REACT_APP_GOOGLE_TTS_API_KEY;
    if (!apiKey) {
      console.error('Google Cloud TTS API key not found in .env file.');
      return; // Exit if the API key is not configured
    }

    // Define voice configurations for supported languages
    const voiceConfigs = {
      ms: { languageCode: 'ms-MY', name: 'ms-MY-Standard-B' }, // Malay Male Voice
      zh: { languageCode: 'cmn-CN', name: 'cmn-CN-Standard-B' },  // Chinese (Mandarin) Male Voice
      ur: { languageCode: 'ur-PK', name: 'ur-PK-Standard-B' }   // Urdu Male Voice
    };

    const voiceConfig = voiceConfigs[language];
    if (!voiceConfig) {
      console.error(`Unsupported language for Google Cloud TTS: ${language}`);
      return;
    }

    const ttsApiUrl = `https://texttospeech.googleapis.com/v1/text:synthesize?key=${apiKey}`;
    const requestBody = {
      input: { text },
      voice: voiceConfig,
      audioConfig: { audioEncoding: 'MP3' },
    };

    try {
      const response = await fetch(ttsApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Google Cloud TTS API request failed:', errorData);
        return;
      }

      const data = await response.json();
      // The API returns audio content as a base64-encoded string.
      // We create a data URL from it to play in the browser.
      const audioSrc = `data:audio/mp3;base64,${data.audioContent}`;
      audioElement.src = audioSrc;
      
      const playPromise = audioElement.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.error('High-quality audio playback failed:', error);
        });
      }
    } catch (error) {
      console.error('Error calling Google Cloud TTS API:', error);
    }
  },
  
  // Try different voice options in sequence
  tryVoiceOptions: (audioElement, encodedText, index = 0) => {
    // Build voice options array with premium male voices (excluding Brian)
    const voiceOptions = PREMIUM_MALE_VOICES.map(voice => 
      `${PREMIUM_TTS_URL}?voice=${voice}&text=${encodedText}`
    );
    
    // Add Google TTS as ultimate fallback
    voiceOptions.push(
      `https://translate.google.com/translate_tts?ie=UTF-8&client=tw-ob&tl=en-US&textlen=${encodedText.length}&q=${encodedText}`
    );
    
    if (index >= voiceOptions.length) {
      console.error('All voice options failed');
      return;
    }
    
    // Set audio source
    audioElement.src = voiceOptions[index];
    
    // Try to play
    const playPromise = audioElement.play();
    if (playPromise !== undefined) {
      playPromise.then(() => {
        const voiceName = index < PREMIUM_MALE_VOICES.length ? 
          PREMIUM_MALE_VOICES[index] : 'Fallback';
        console.log(`Playing premium male voice: ${voiceName}`);
      }).catch(error => {
        console.error(`Voice option ${index + 1} failed:`, error);
        // Try next voice option
        VoiceHelper.tryVoiceOptions(audioElement, encodedText, index + 1);
      });
    }
  },
  
  // Process text to improve speech quality
  processTextForSpeech: (text) => {
    if (!text) return '';
    
    // Replace common abbreviations
    const replacements = {
      'w/': 'with ',
      'w/o': 'without ',
      'govt': 'government ',
      'govt.': 'government ',
      'min.': 'minutes ',
      'mins.': 'minutes ',
      'hr.': 'hour ',
      'hrs.': 'hours ',
      'approx.': 'approximately ',
      'e.g.': 'for example ',
      'i.e.': 'that is ',
      'etc.': 'etcetera ',
      'vs.': 'versus ',
      'est.': 'established ',
      'dept.': 'department ',
    };
    
    let processedText = text;
    
    // Apply replacements
    Object.keys(replacements).forEach(abbr => {
      const regex = new RegExp(`\\b${abbr}\\b`, 'gi');
      processedText = processedText.replace(regex, replacements[abbr]);
    });
    
    // Fix spacing issues
    processedText = processedText
      .replace(/\s+/g, ' ')
      .replace(/\s([.,;:!?])/g, '$1')
      .trim();
    
    return processedText;
  }
};

export default VoiceHelper;
